<div class="schedule-checklist-detail"
     [class.search-spotlight-active]="!!spotlightAssertionIdentifier()"
     data-testid="schedule-checklist-detail">
  <!-- The schedule-detail-header is a child component; test IDs within it should be managed in its own template -->
  <schedule-detail-header [title]="areaName"
                          [subtitle]="reviewedAssertionsText()"
                          [backCallback]="goBack.bind(this)"
                          [showCloud]="true"
                          [isRefreshingInBackground]="isRefreshingInBackground()" />

  <pill-toggle [options]="filters"
               [activeValue]="activeFilter()"
               class="schedule-checklist-filters"
               [class.faded]="!dataLoaded"
               (onToggleChanged)="setActiveFilter($event)"
               aria-label="Filter checklist items"
               data-testid="schedule-checklist-filters">
    <ng-template let-option>
      {{ option.label }}
      <ng-container *ngIf="option.value === 'pending'"> ({{ pendingAssertionsCount() }})</ng-container>
    </ng-template>
  </pill-toggle>

  <div class="schedule-checklist-content"
       [appDataUpdatedFade]="dataUpdatedPulse()"
       data-testid="schedule-checklist-content">
    <div class="schedule-checklist">
      <div class="search-btn-overlay">
        @if (dataLoaded) {
          <!-- Accessibility: role="region" creates a landmark for each component's section. aria-labelledby links it to the visible heading. -->
          @for (component of activeComponents(); track $index) {
            <section attr.id="component-{{component.id}}"
                     class="checklist-component"
                     [attr.aria-labelledby]="'component-heading-' + component.id"
                     [attr.data-testid]="'checklist-component-' + component.id">
              <p class="component-heading"
                 [id]="'component-heading-' + component.id"
                 [attr.data-testid]="'component-heading-' + component.id">
                {{component.name}}
              </p>
              @for (assertion of filteredAssertionsByComponent[component.id!]();track $index) {
                <div attr.id="assertion-{{assertion.identifier}}"
                     class="checklist-component-assertion"
                     [class.active-card]="getAssertionStatus(assertion) === 'notCompleted'"
                     [class.newly-added]="newComponentID === component.id"
                     [class.search-spotlight-target]="isSpotlightedAssertion(assertion)"
                     [class.carryover-fading]="carryoverFadingAssertionId() === assertion.id"
                     [attr.data-testid]="'checklist-component-assertion-' + assertion.id"
                     [ngStyle]="{
                      'background-color': isAssertionReviewed(assertion) ? 'var(--gray-50)' : 'white',
                      'padding': isAssertionReviewed(assertion) ? '.75rem' : '1rem'
                    }">
                  <ng-container [ngSwitch]="getAssertionStatus(assertion)">
                    <ng-container *ngSwitchCase="'na'">
                      <div class="completed-assertion"
                           [attr.data-testid]="'na-assertion-' + assertion.id"
                           [attr.aria-label]="assertion.text + ', status: N/A.'">
                        <span class="na-badge">N/A</span>
                        <div class="info">
                          <p class="assertion-text-font"
                             [innerHTML]="assertion.text"></p>
                          <a class="remove-na-link"
                             (click)="removeNA(assertion)"
                             [attr.data-testid]="'remove-na-button-' + assertion.id"
                             [attr.aria-label]="'Remove N/A for ' + assertion.text">Remove N/A</a>
                        </div>
                      </div>
                    </ng-container>
                    <ng-container *ngSwitchCase="'completed'">
                      <!-- Accessibility: role="button" and tabindex="0" make this div keyboard accessible. aria-label provides full context. -->
                      <div class="completed-assertion"
                           role="button"
                           tabindex="0"
                           [attr.aria-label]="assertion.text + ', status: completed. Click to view details.'"
                           (click)="navToLooksGoodDetails(assertion)"
                           (keydown.enter)="navToLooksGoodDetails(assertion)"
                           [class.faded]="currentAction() != 'Viewing'"
                           [attr.data-testid]="'completed-assertion-' + assertion.id">
                        <span class="icon status-icon icon-check"
                              aria-hidden="true"></span>
                        <div class="info">
                          <p class="assertion-text-font"
                             [innerHTML]="assertion.text"></p>
                          @let imageCount = imageCounts.get(assertion.id!);
                          @if (imageCount && imageCount > 0) {
                            <a>{{ imageCount }} Images attached</a>
                          }
                        </div>
                        <i class="pi pi-chevron-right"
                           aria-hidden="true"></i>
                      </div>
                    </ng-container>
                    <ng-container *ngSwitchCase="'needsAttention'">
                      <div class="needs-attention-assertion"
                           role="button"
                           tabindex="0"
                           [attr.aria-label]="assertion.text + ', status: needs attention. Click to view items.'"
                           (click)="navToIssueList(assertion)"
                           (keydown.enter)="navToIssueList(assertion)"
                           [class.faded]="currentAction() != 'Viewing'"
                           [attr.data-testid]="'needs-attention-assertion-' + assertion.id">
                        <span class="icon status-icon icon-warning"
                              aria-hidden="true"></span>
                        <div class="info">
                          <p class="assertion-text-font"
                             [innerHTML]="assertion.text"></p>
                          <a>{{issuesByAssertion[assertion.identifier]().length}} Attention Items</a>
                        </div>
                        <i class="pi pi-chevron-right"
                           aria-hidden="true"></i>
                      </div>
                    </ng-container>
                    <ng-container *ngSwitchDefault>
                      @let previousIssueCount = isCcmInspection() ? getPreviousIssueCount(assertion) : 0;
                      <p class="assertion-text-font"
                         [id]="'assertion-text-' + assertion.id"
                         [innerHTML]="assertion.text"></p>
                      <div class="assertion-actions"
                           role="group"
                           [attr.aria-labelledby]="'assertion-text-' + assertion.id">
                        <button class="good-button"
                                (click)="setLooksGood(assertion)"
                                [disabled]="processingAssertionId() === assertion.id"
                                [attr.data-testid]="'looks-good-button-' + assertion.id"
                                [attr.aria-label]="'Mark ' + assertion.text + ' as Looks Good'">
                          @if (updatingAssertionAction() === AssertionAction.LooksGood && processingAssertionId() === assertion.id) {
                        <span class="pi pi-spin pi-spinner"
                              aria-hidden="true"></span>
                      } @else {
                        <span class="icon icon-check"
                              aria-hidden="true"></span>
                      }
                      </button>
                      <button class="issue-button"
                              (click)="addIssue(assertion)"
                              [disabled]="processingAssertionId() === assertion.id"
                              [attr.data-testid]="'needs-attention-button-' + assertion.id"
                              [attr.aria-label]="previousIssueCount > 0
                                  ? ('Review ' + previousIssueCount + ' carried-over items for ' + assertion.text)
                                  : ('Mark ' + assertion.text + ' as Needs Attention')">
                        <span class="issue-icon-badge">
                          @if (updatingAssertionAction() === AssertionAction.NeedsAttention && processingAssertionId() === assertion.id) {
                            <span class="pi pi-spin pi-spinner"
                                  aria-hidden="true"></span>
                          } @else {
                            <span class="icon icon-warning"
                                  aria-hidden="true"></span>
                            @if (previousIssueCount > 0) {
                              <span class="previous-issue-badge"
                                    [attr.data-testid]="'previous-issue-badge-' + assertion.id">
                                <p-badge [value]="previousIssueCount.toString()"
                                         severity="danger"
                                         badgeSize="small" />
                              </span>
                            }
                          }
                        </span>
                      </button>
                      <button class="na-button"
                              (click)="markAsNA(assertion)"
                              [disabled]="processingAssertionId() === assertion.id"
                              [attr.data-testid]="'na-button-' + assertion.id"
                              [attr.aria-label]="'Mark ' + assertion.text + ' as N/A'">
                        @if (updatingAssertionAction() === AssertionAction.NotApplicable && processingAssertionId() === assertion.id) {
                          <span class="pi pi-spin pi-spinner"
                                aria-hidden="true"></span>
                        } @else {
                          N/A
                        }
                      </button>
                </div>
                </ng-container>
                </ng-container>
      </div>
    }
    </section>
  }
  @if (dataLoaded && activeFilter() !== 'reviewed' && activeFilter() !== 'needs-attention') {
    <button class="add-feature-button"
            (click)="openAddComponentDialog()"
            [attr.data-testid]="'add-feature-button'">
      <img src="assets/icons/add.svg"
           alt=""
           class="add-icon"
           aria-hidden="true">
      <div class="add-feature-text">
        <span class="add-feature-main">Add a feature</span>
        <span class="add-feature-sub">(e.g. Fireplace, HVAC, Appliances)</span>
      </div>
    </button>
  }
} @else {
  <!-- Accessibility: aria-live="polite" announces when content replaces this loader. aria-busy="true" indicates it's actively loading. -->
  <div class="checklist-skeleton-container"
       aria-live="polite"
       aria-busy="true"
       aria-label="Loading checklist items"
       data-testid="checklist-skeleton-container">
    <p-skeleton width="6rem"></p-skeleton>
    <p-skeleton height="140px"></p-skeleton>
    <p-skeleton height="140px"></p-skeleton>
  </div>
}
<div class="search-btn-container schedule-checklist-search-btn-container">
  @if (dataLoaded && currentAction() === 'Viewing' && !searchVisible()  && !bottomSheetState.hasOpenSheet()) {
    <p-button type="button"
              icon="pi pi-search"
              styleClass="job-areas-search-fab schedule-checklist-search-fab"
              [rounded]="true"
              (onClick)="openSearch()"
              ariaLabel="Search inspection items"
              data-testid="schedule-checklist-btn-open-search" />
  }
  @if (searchVisible()) {
    <app-inspection-item-search [items]="searchableInspectionItems()"
                                (closeSearch)="closeSearch()"
                                (selectSearchResult)="navigateToSearchResult($event)">
    </app-inspection-item-search>
  }
</div>
    </div>
  </div>
</div>
</div>

<app-bottom-sheet [isOpen]="addingComponent"
                  [leftButtonText]="'Cancel'"
                  [rightButtonText]="'Add Detail'"
                  [leftButtonDisabled]="false"
                  [rightButtonDisabled]="!newComponentForm.valid"
                  [preventOverlayClose]="true"
                  [headerTemplate]="addComponentHeaderTemplate"
                  [contentTemplate]="addComponentContentTemplate"
                  (isOpenChange)="addingComponent = $event"
                  (leftButtonClick)="onAddComponentCancel()"
                  (rightButtonClick)="onAddComponentSubmit()"
                  data-testid="add-component-bottom-sheet">
</app-bottom-sheet>

<ng-template #addComponentHeaderTemplate>
  <div class="dialog-header"
       data-testid="dialog-header">
    <div id="dialog-title"
         class="header-md-bold"
         data-testid="dialog-add-room-header">Add a feature</div>
    <div id="dialog-subtitle"
         class="body-sm-regular dialog-zone-name"
         data-testid="dialog-zone-name">{{areaName}}</div>
  </div>
</ng-template>

<ng-template #addComponentContentTemplate>
  <form [formGroup]="newComponentForm"
        novalidate>
    <div class="component-content"
         data-testid="component-content"
         aria-live="polite"
         aria-label="Component list">
      <div class="components-list"
           data-testid="components-list">
        @if (componentPresets().length === 0) {
          <p-skeleton height="44px"
                      border-radius="4px"></p-skeleton>
          <p-skeleton height="44px"
                      border-radius="4px"></p-skeleton>
          <p-skeleton height="44px"
                      border-radius="4px"></p-skeleton>
          <p-skeleton height="44px"
                      border-radius="4px"></p-skeleton>
          <p-skeleton height="44px"
                      border-radius="4px"></p-skeleton>
        }

        @for (component of componentPresets(); track $index) {
          <div class="component-item"
               [attr.data-testid]="'component-item-' + $index">
            <label class="component-radio-label"
                   [class.selected]="newComponentForm.value.selectedPreset === $index"
                   [attr.data-testid]="'component-radio-label-' + $index">
              <input type="radio"
                     name="selectedPreset"
                     [value]="$index"
                     [checked]="newComponentForm.value.selectedPreset === $index"
                     (change)="selectComponent($index)"
                     formControlName="selectedPreset"
                     class="component-radio"
                     [attr.data-testid]="'component-radio-' + $index" />
              <div class="component-info"
                   [attr.data-testid]="'component-info-' + $index">
                <div class="component-details"
                     [attr.data-testid]="'component-details-' + $index">
                  <span class="component-name"
                        [attr.data-testid]="'component-name-' + $index">{{ component.name }}</span>
                </div>
              </div>
            </label>
          </div>
        }
      </div>
    </div>
  </form>
</ng-template>

<app-previous-issue-container [(isOpen)]="previousIssueContainerOpen"
                              [assertionText]="previousIssueContainerAssertionText()"
                              [issues]="previousIssueContainerIssues()"
                              (confirmIssues)="onPreviousIssuesConfirmed($event)"
                              (dismissIssues)="onPreviousIssuesDismissed($event)"
                              (addNewIssue)="onPreviousIssuesAddNew()"
                              data-testid="schedule-checklist-previous-issue-container" />